import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LiveKitRoom, VideoConference, RoomAudioRenderer, useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { LiveStreamControls, useVideoFilter } from '../components/LiveStreamControls';
import LiveReplayModal from '../components/LiveReplayModal';
import { HostControlsPanel, ViewerJoinButton, GuestControlsBar } from '../components/LiveGuestSystem';
import MediaPermissionPrompt from '../components/MediaPermissionPrompt';
import LiveReactionPanel from '../components/LiveReactionPanel';

import {
  Radio, Users, Clock, AlertCircle, Loader2, PhoneOff,
  CheckCircle2, XCircle, RefreshCw, Wifi, WifiOff,
  Eye, MessageCircle, Heart, Gift, Share2, ArrowLeft,
  Camera, Mic, MonitorPlay
} from 'lucide-react';

function LiveStreamContent({ isHost }) {
  const room = useRoomContext();
  const { filter, setFilter } = useVideoFilter();

  const handleCameraSwitch = useCallback(async (deviceId) => {
    try {
      if (room) {
        await room.switchActiveDevice('videoinput', deviceId);
      }
    } catch (error) {
      console.error('[LiveStreamContent] Camera switch error:', error);
      throw error;
    }
  }, [room]);

  const handleFilterChange = useCallback((filterValue) => {
    setFilter(filterValue);
  }, [setFilter]);

  return (
    <>
      <div className="h-full" style={{ filter: filter !== 'none' ? filter : undefined }} data-testid="live-video-container">
        <VideoConference />
      </div>
      <RoomAudioRenderer />
      {isHost && (
        <LiveStreamControls
          onCameraSwitch={handleCameraSwitch}
          onFilterChange={handleFilterChange}
          disabled={false}
        />
      )}
    </>
  );
}


function PreflightCheck({ onReady, onSkip }) {
  const { getAuthHeaders } = useAuth();
  const [checks, setChecks] = useState({
    camera: 'pending',
    microphone: 'pending',
    network: 'pending',
    server: 'pending',
  });
  const [running, setRunning] = useState(true);
  const [allPassed, setAllPassed] = useState(false);

  useEffect(() => {
    runChecks();
  }, []);

  const runChecks = async () => {
    setRunning(true);
    const results = { ...checks };

    // Camera check
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      results.camera = 'pass';
    } catch {
      results.camera = 'fail';
    }
    setChecks({ ...results });

    // Mic check
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      results.microphone = 'pass';
    } catch {
      results.microphone = 'fail';
    }
    setChecks({ ...results });

    // Network check
    try {
      const start = Date.now();
      await fetch(`${API}/health`, { signal: AbortSignal.timeout(5000) });
      const latency = Date.now() - start;
      results.network = latency < 3000 ? 'pass' : 'warn';
    } catch {
      results.network = 'fail';
    }
    setChecks({ ...results });

    // Server preflight
    try {
      const res = await fetch(`${API}/live-system/preflight-check`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        results.server = data.can_go_live ? 'pass' : 'fail';
      } else {
        results.server = 'fail';
      }
    } catch {
      results.server = 'fail';
    }
    setChecks({ ...results });

    const passed = Object.values(results).every(v => v === 'pass' || v === 'warn');
    setAllPassed(passed);
    setRunning(false);
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'pass': return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case 'warn': return <AlertCircle className="w-5 h-5 text-amber-400" />;
      case 'fail': return <XCircle className="w-5 h-5 text-red-400" />;
      default: return <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />;
    }
  };

  const labels = {
    camera: { label: 'Camera', icon: Camera },
    microphone: { label: 'Microphone', icon: Mic },
    network: { label: 'Network', icon: Wifi },
    server: { label: 'Server Ready', icon: MonitorPlay },
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="preflight-check">
      <Card className="max-w-md w-full glass-card border-border p-6">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-3">
            <Radio className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="text-xl font-bold">Pre-flight Check</h1>
          <p className="text-sm text-muted-foreground mt-1">Verifying everything is ready for your broadcast</p>
        </div>

        <div className="space-y-3 mb-6">
          {Object.entries(labels).map(([key, { label, icon: Icon }]) => (
            <div key={key} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border"
              data-testid={`preflight-${key}`}>
              <div className="flex items-center gap-3">
                <Icon className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{label}</span>
              </div>
              {statusIcon(checks[key])}
            </div>
          ))}
        </div>

        {checks.camera === 'fail' && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-sm text-red-400">
            Camera access denied. Check your browser permissions or try audio-only mode.
          </div>
        )}

        <div className="flex gap-2">
          {allPassed && (
            <Button className="flex-1 bg-red-500 hover:bg-red-600" onClick={onReady} data-testid="preflight-go-btn">
              <Radio className="w-4 h-4 mr-2" /> Continue to Broadcast
            </Button>
          )}
          {!allPassed && !running && (
            <>
              <Button variant="outline" className="flex-1 border-border" onClick={runChecks} data-testid="preflight-retry-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
              {checks.camera === 'fail' && (
                <Button variant="outline" className="flex-1 border-amber-500/30 text-amber-400" onClick={onSkip}
                  data-testid="preflight-audio-only-btn">
                  <Mic className="w-4 h-4 mr-2" /> Audio Only
                </Button>
              )}
            </>
          )}
          {running && (
            <div className="flex-1 flex items-center justify-center py-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking...
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}


function PostLiveScreen({ streamId, stream, onNavigate }) {
  const { getAuthHeaders } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/live-system/post-live/${streamId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setSummary(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [streamId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const data = summary || {};
  const viewers = data.viewers || {};
  const engagement = data.engagement || {};
  const replay = data.replay || {};

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="post-live-screen">
      <Card className="max-w-lg w-full glass-card border-border p-6">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Radio className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold">Live Ended</h1>
          <p className="text-muted-foreground text-sm mt-1">{data.title || stream?.title || 'Your stream'}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <StatCard icon={Clock} label="Duration" value={data.duration_formatted || '0m'} />
          <StatCard icon={Eye} label="Peak Viewers" value={viewers.peak || 0} />
          <StatCard icon={Heart} label="Reactions" value={engagement.reactions || 0} />
          <StatCard icon={MessageCircle} label="Chat Messages" value={engagement.chat_messages || 0} />
          {engagement.gifts_total > 0 && (
            <StatCard icon={Gift} label="Gifts" value={`$${engagement.gifts_total.toFixed(2)}`} />
          )}
          {engagement.guests > 0 && (
            <StatCard icon={Users} label="Guests" value={engagement.guests} />
          )}
        </div>

        {/* Replay Status */}
        <div className={`rounded-lg p-3 mb-6 border text-sm ${
          replay.saved ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' :
          replay.status === 'processing' ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' :
          'bg-muted/30 border-border text-muted-foreground'
        }`} data-testid="replay-status">
          {replay.saved ? (
            <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Replay saved successfully</span>
          ) : replay.status === 'processing' ? (
            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Replay processing...</span>
          ) : (
            <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4" /> No replay available</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {replay.saved && replay.replay_id && (
            <Button variant="outline" className="w-full border-border" onClick={() => onNavigate(`/replay/${replay.replay_id}`)}
              data-testid="view-replay-btn">
              <MonitorPlay className="w-4 h-4 mr-2" /> View Replay
            </Button>
          )}
          <Button variant="outline" className="w-full border-border" data-testid="share-replay-btn"
            onClick={() => {
              navigator.clipboard.writeText(window.location.origin + `/watch/${streamId}`);
              toast.success('Link copied!');
            }}>
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
          <Button className="w-full" onClick={() => onNavigate('/dashboard')} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-muted/20 border border-border rounded-lg p-3 text-center">
      <Icon className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
  );
}


function ConnectionOverlay({ state }) {
  if (state === 'connected') return null;

  return (
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-40" data-testid="connection-overlay">
      {state === 'connecting' && (
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-white font-medium">Connecting...</p>
        </div>
      )}
      {state === 'reconnecting' && (
        <div className="text-center">
          <RefreshCw className="w-10 h-10 animate-spin text-amber-400 mx-auto mb-3" />
          <p className="text-white font-medium">Reconnecting...</p>
          <p className="text-white/60 text-sm mt-1">Please wait...</p>
        </div>
      )}
      {state === 'disconnected' && (
        <div className="text-center">
          <WifiOff className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium">Connection Lost</p>
          <p className="text-white/60 text-sm mt-1">Attempting to reconnect...</p>
        </div>
      )}
    </div>
  );
}


export default function GoLivePage() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stream, setStream] = useState(null);
  const [token, setToken] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [showReplayModal, setShowReplayModal] = useState(false);
  const [endedStreamId, setEndedStreamId] = useState(null);
  const [streamEnded, setStreamEnded] = useState(false);
  const [error, setError] = useState(null);
  const [preflightPassed, setPreflightPassed] = useState(false);
  const [audioOnly, setAudioOnly] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Timer for live duration
  useEffect(() => {
    if (stream?.status !== 'live') return;
    const interval = setInterval(() => setElapsedTime(e => e + 1), 1000);
    return () => clearInterval(interval);
  }, [stream?.status]);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (streamId) initializeStream();
  }, [streamId, isAuthenticated]);

  const initializeStream = async () => {
    try {
      const streamRes = await fetch(`${API}/live/streams/${streamId}`, { headers: getAuthHeaders() });
      if (!streamRes.ok) {
        const errData = await streamRes.json().catch(() => ({}));
        throw new Error(errData?.detail || `Stream not found (${streamRes.status})`);
      }
      const streamData = await streamRes.json();
      if (!streamData?.stream_id) throw new Error('Invalid stream data received');
      setStream(streamData);

      const tokenRes = await fetch(`${API}/live/token/${streamId}`, { headers: getAuthHeaders() });
      if (!tokenRes.ok) {
        const errData = await tokenRes.json().catch(() => ({}));
        throw new Error(errData?.detail || `Failed to get stream token (${tokenRes.status})`);
      }
      const tokenData = await tokenRes.json();
      if (!tokenData?.token || !tokenData?.url) throw new Error('Missing token or server URL');
      setToken(tokenData.token);
      setServerUrl(tokenData.url);
    } catch (err) {
      const msg = (err instanceof Error) ? err.message : 'Failed to initialize stream';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoLive = async () => {
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/start`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setStream(prev => ({ ...prev, status: 'live' }));
        setElapsedTime(0);
        toast.success("You're now LIVE!");
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData?.detail || 'Failed to go live');
      }
    } catch {
      toast.error('Failed to start stream');
    }
  };

  const handleEndStream = async () => {
    if (!window.confirm('Are you sure you want to end the stream?')) return;
    try {
      const response = await fetch(`${API}/live/streams/${streamId}/end`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        toast.success('Stream ended');
        setConnectionState('disconnected');
        setToken(null);
        setStreamEnded(true);
        setEndedStreamId(streamId);
      } else {
        const errData = await response.json().catch(() => ({}));
        toast.error(errData?.detail || 'Failed to end stream');
      }
    } catch {
      toast.error('Failed to end stream');
      setStreamEnded(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    }
  };

  const formatTime = (secs) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Preparing broadcast...</p>
        </div>
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full glass-card border-red-500/30 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Unable to Start Broadcast</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  // Stream Ended — Post-Live Screen
  if (streamEnded) {
    return <PostLiveScreen streamId={endedStreamId || streamId} stream={stream} onNavigate={navigate} />;
  }

  // No token (not configured)
  if (!token || !serverUrl) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full glass-card border-amber-500/30 p-6 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2">Live Streaming Not Available</h1>
          <p className="text-muted-foreground mb-4">LiveKit connection could not be established. Please try again.</p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.reload()} className="flex-1">
              <RefreshCw className="w-4 h-4 mr-2" /> Retry
            </Button>
            <Button onClick={() => navigate('/dashboard')} className="flex-1">Back</Button>
          </div>
        </Card>
      </div>
    );
  }

  const isHost = stream?.host_user_id === user?.user_id;

  // Pre-flight check for host
  if (isHost && !preflightPassed) {
    return (
      <PreflightCheck
        onReady={() => setPreflightPassed(true)}
        onSkip={() => { setAudioOnly(true); setPreflightPassed(true); }}
      />
    );
  }

  return (
    <div className="h-screen bg-black flex flex-col" data-testid="go-live-page">
      {/* Header */}
      <div className="bg-background/80 backdrop-blur-md border-b border-border p-3 flex items-center justify-between relative z-50">
        <div className="flex items-center gap-3">
          {stream?.status === 'live' ? (
            <div className="flex items-center gap-2">
              <Badge className="bg-red-500 text-white animate-pulse" data-testid="live-indicator">
                <Radio className="w-3 h-3 mr-1" /> LIVE
              </Badge>
              <span className="text-xs text-red-300 font-mono" data-testid="live-timer">{formatTime(elapsedTime)}</span>
            </div>
          ) : (
            <Badge variant="outline" className="border-amber-500/50 text-amber-400">
              <Clock className="w-3 h-3 mr-1" /> Preparing
            </Badge>
          )}
          <h1 className="font-heading font-bold truncate max-w-[300px]">{stream?.title}</h1>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-sm text-muted-foreground" data-testid="viewer-count">
            <Users className="w-4 h-4" />
            <span>{stream?.viewer_count || 0}</span>
          </div>

          {!isHost && stream?.status === 'live' && <GuestControlsBar streamId={streamId} onLeave={() => navigate('/live')} />}
          {!isHost && stream?.status === 'live' && <ViewerJoinButton streamId={streamId} />}

          {isHost && stream?.status !== 'live' && (
            <Button onClick={handleGoLive} className="bg-red-500 hover:bg-red-600" data-testid="go-live-btn">
              <Radio className="w-4 h-4 mr-2" /> Go Live
            </Button>
          )}
          {isHost && stream?.status === 'live' && (
            <Button variant="destructive" onClick={handleEndStream} data-testid="end-stream-btn">
              <PhoneOff className="w-4 h-4 mr-2" /> End Stream
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex relative z-10">
        <div className="flex-1 relative">
          <ConnectionOverlay state={connectionState} />
          <LiveKitRoom
            token={token}
            serverUrl={serverUrl}
            connect={true}
            video={!audioOnly}
            audio={true}
            onConnected={() => setConnectionState('connected')}
            onDisconnected={() => setConnectionState('disconnected')}
            onReconnecting={() => setConnectionState('reconnecting')}
            onReconnected={() => setConnectionState('connected')}
            style={{ height: '100%' }}
          >
            <LiveStreamContent isHost={isHost} />
          </LiveKitRoom>
        </div>

        {stream?.status === 'live' && (
          <div className="w-64 border-l border-border bg-background/60 backdrop-blur-md overflow-y-auto hidden lg:block">
            <HostControlsPanel streamId={streamId} isHost={isHost} />
          </div>
        )}
      </div>

      {stream?.status === 'live' && (
        <LiveReactionPanel streamId={streamId} isHost={isHost} />
      )}
    </div>
  );
}
